import { defineStore } from 'pinia';
import { computed } from 'vue';

import { useEditingIngredientStore } from '@apps/main/modules/store/editingIngredientStore';
import {
  MapStateKey,
  ProductDetailStateKey,
  RecipeStateKey,
  TripStateKey,
} from '@apps/main/modules/state/state';
import { usePreferredTimeStore } from '@apps/main/modules/store/preferredTimeStore';

import type {
  DetailsStaticInfo,
  HeartedHotel,
  IngredientId,
} from '@libs/trip-planner-interfaces';
import {
  BoardBasisCode,
  ProductSearchType,
} from '@libs/trip-planner-interfaces';
import type { AppMultiSelectOption } from '@libs/ui';
import { injectStrict } from '@libs/utils';

import type {
  HotelStaticData,
  HotelDynamicData,
  SelectStayEventPayload,
  ToggleHeartEventPayload,
} from '../../modules/hotelDetails';
import type { HotelFilterMealsUpdate } from '../../modules/hotelFilters';
import type { RemoveHotelSelectionFiltersOrigin } from '../models';
import { getBoardBasisTextNoIncluded } from '../helpers/translation.helper';

import { useRouterStore } from './routerStore';
import { useFiltersStore } from './filters/filtersStore';

export const useHotelDetailsStore = defineStore('HotelDetailsStore', () => {
  const mapState = injectStrict(MapStateKey);
  const productDetailsState = injectStrict(ProductDetailStateKey);
  const tripState = injectStrict(TripStateKey);
  const recipeState = injectStrict(RecipeStateKey);
  const editingIngredientStore = useEditingIngredientStore();
  const routerStore = useRouterStore();
  const preferredTimeStore = usePreferredTimeStore();
  const filtersStore = useFiltersStore();

  // Actions
  function openHotelDetails(
    hotelCode: string,
    ingredientId: IngredientId,
    shouldScrollToAvailability = false
  ): void {
    productDetailsState.shouldScrollToAvailability.value =
      shouldScrollToAvailability;
    productDetailsState.selectDestinationProduct(
      hotelCode,
      ingredientId,
      ProductSearchType.Accommodation
    );
  }

  // NOTE: Temporary method required until we will be able to
  // use hotel details panel on separated route on production.
  function openHotelDetailsForce(
    hotelCode: string,
    ingredientId: IngredientId,
    shouldScrollToAvailability = false
  ): void {
    productDetailsState.shouldScrollToAvailability.value =
      shouldScrollToAvailability;
    productDetailsState.selectDestinationProduct(
      hotelCode,
      ingredientId,
      ProductSearchType.Accommodation
    );
  }

  function selectStay(eventPayload: SelectStayEventPayload): void {
    const heartedHotel: HeartedHotel = {
      code: eventPayload.hotelCode,
      isRefundable: eventPayload.stayId.isRefundable,
      rooms: eventPayload.stayId.hotelRoomIds.map((room) => ({
        code: room.roomCode,
        boardBasis: room.boardBasis,
        roomTypeId: room.roomTypeId,
      })),
    };
    tripState.heartHotel(heartedHotel, eventPayload.ingredientId);
    mapState.mapShouldCenterOnMarkers.value = true;
    editingIngredientStore.doneEditingIngredient();
  }

  function toggleHeart(
    eventPayload: ToggleHeartEventPayload,
    finalizeEdit = true
  ): void {
    const heartedHotel: HeartedHotel = {
      code: eventPayload.hotelCode,
    };
    tripState.heartHotel(heartedHotel, eventPayload.ingredientId);

    if (finalizeEdit) {
      editingIngredientStore.doneEditingIngredient();
    }
  }

  function suggestAnotherDay(): void {
    routerStore.navigateToTripOverview();
    preferredTimeStore.clearPreferredDay();
  }

  function getMealFitlerOptions(): AppMultiSelectOption<BoardBasisCode>[] {
    const allBoardBasisOptions: AppMultiSelectOption<BoardBasisCode>[] = [
      {
        id: BoardBasisCode.RO,
        label: getBoardBasisTextNoIncluded(BoardBasisCode.RO),
        selected: isBoardBasisSelected(BoardBasisCode.RO),
      },
      {
        id: BoardBasisCode.BB,
        label: getBoardBasisTextNoIncluded(BoardBasisCode.BB),
        selected: isBoardBasisSelected(BoardBasisCode.BB),
      },
      {
        id: BoardBasisCode.HB,
        label: getBoardBasisTextNoIncluded(BoardBasisCode.HB),
        selected: isBoardBasisSelected(BoardBasisCode.HB),
      },
      {
        id: BoardBasisCode.FB,
        label: getBoardBasisTextNoIncluded(BoardBasisCode.FB),
        selected: isBoardBasisSelected(BoardBasisCode.FB),
      },
      {
        id: BoardBasisCode.AI,
        label: getBoardBasisTextNoIncluded(BoardBasisCode.AI),
        selected: isBoardBasisSelected(BoardBasisCode.AI),
      },
    ];

    return allBoardBasisOptions;
  }

  function isBoardBasisSelected(boardBasis: BoardBasisCode): boolean {
    return filtersStore.isBoardBasisSelected(boardBasis);
  }

  function updateMealFilter(event: {
    filter: HotelFilterMealsUpdate;
    source: RemoveHotelSelectionFiltersOrigin;
  }): void {
    filtersStore.updateHotelFilter(event.filter, event.source);
  }

  // Getters
  const hotelDetailsStaticData = computed<HotelStaticData | undefined>(() => {
    const staticHotelData =
      productDetailsState.destinationStaticProductDetailForHotel.value;

    if (!staticHotelData) {
      return;
    }

    return {
      ingredientId: staticHotelData.ingredientId,
      hotelCode: staticHotelData.productCode,
      isAvailabilityLoading: staticHotelData.isAvailabilityLoading,
      staticInfo: staticHotelData.staticInfo as DetailsStaticInfo,
      isHearted: staticHotelData.isHearted,
      isInItinerary: staticHotelData.isInItinerary,
      numberOfNights: staticHotelData.numberOfNights,
      scrollToAvailability:
        productDetailsState.shouldScrollToAvailability.value,
      destinationInformation: tripState.getDestinationInformation(
        staticHotelData.ingredientId
      ),
      isSingleHotelRecipe:
        recipeState.recipe.value.length === 1 &&
        recipeState.hotelOnlyRecipe.value.length === 1,
    };
  });

  const hotelDetailsDynamicData = computed<HotelDynamicData | undefined>(() => {
    const hotelDynamicData =
      productDetailsState.destinationDynamicProductDetailForHotel.value;

    if (!hotelDynamicData) {
      return;
    }

    return {
      availability: hotelDynamicData.availability,
      isMetaHotel: hotelDynamicData.availability.some(
        (bookableOption) => bookableOption.isFromMeta
      ),
    };
  });

  return {
    // Actions
    openHotelDetails,
    openHotelDetailsForce,
    selectStay,
    toggleHeart,
    suggestAnotherDay,
    getMealFitlerOptions,
    updateMealFilter,

    // Getters

    hotelDetailsStaticData,
    hotelDetailsDynamicData,
  };
});
